import { useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'

export interface TOTPDisableModalRef {
  open: () => void
  close: () => void
}

interface TOTPDisableModalProps {
  onSuccess?: () => void
}

const TOTPDisableModal = forwardRef<TOTPDisableModalRef, TOTPDisableModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('settings')
    const [isOpen, setIsOpen] = useState(false)
    const [password, setPassword] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true)
        setPassword('')
        setError('')
      },
      close: () => {
        setIsOpen(false)
        setPassword('')
        setError('')
      },
    }))

    const handleClose = () => {
      if (isSubmitting) return
      setIsOpen(false)
      setPassword('')
      setError('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      if (!password) {
        setError(t('profile.2fa.disableModal.errors.passwordRequired', 'Password is required'))
        return
      }

      try {
        setIsSubmitting(true)
        setError('')

        await authService.disableTOTP(password)

        setIsOpen(false)
        setPassword('')
        onSuccess?.()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setIsSubmitting(false)
      }
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-linear-to-r from-red-50 to-transparent">
            <h2 className="text-xl font-bold text-gray-900">
              {t('profile.2fa.disableModal.title', 'Disable Two-Factor Authentication')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('profile.2fa.disableModal.subtitle', 'Enter your password to confirm')}
            </p>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6">
              {/* Warning */}
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900">
                      {t('profile.2fa.disableModal.warning', 'Warning')}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {t('profile.2fa.disableModal.warningMessage', 'Disabling 2FA will make your account less secure. You will only need your password to sign in.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Password Input */}
              <div>
                <label htmlFor="disablePassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.2fa.disableModal.password', 'Password')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="disablePassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  placeholder={t('profile.2fa.disableModal.passwordPlaceholder', 'Enter your password')}
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('profile.2fa.disableModal.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !password}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{t('profile.2fa.disableModal.confirm', 'Disable 2FA')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
)

TOTPDisableModal.displayName = 'TOTPDisableModal'

export default TOTPDisableModal
